import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import UserStatistic from './UserStatistic';
//import UserManual from './UserManual'
import CurrentDashboard from './CurrentDashboard';
import Timesheet from './Timesheet';
import MonitoringCash from './MonitoringCash';
import MonitoringKM from './MonitoringKM';
import Documents from './Documents/index';
import UserMarkingCodes from './MarkingCodes';
import GeolocationManager from './GeolocationManager';
import PharmacyMap from './PharmacyMap/index';
import Nomenclature from './Nomenclature';
import UserProfile from './UserProfile';
import DashboardSidebar from './dashboard/DashboardSidebar';
import DashboardHeader from './dashboard/DashboardHeader';
import OnlineJournal from './OnlineJournal/OnlineJournal';
import { useNotifications } from './notifications/useNotifications';
import NotificationPanel from './notifications/NotificationPanel';
import { clearUserSession } from '../auth';

const Dashboard: React.FC = () => {
  const userRole = localStorage.getItem('role');
  const [selectedIndex, setSelectedIndex] = useState(userRole === 'storage' ? 7 : 0);
  const [profileOpen, setProfileOpen] = useState(false);
  const [forceProfileCheck, setForceProfileCheck] = useState(false);
  const [username, setUsername] = useState('');
  const [pharmacyNum, setPharmacyNum] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [error, setError] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [lockTimer, setLockTimer] = useState<string>('');
  const [lastLogoutTime, setLastLogoutTime] = useState<Date | null>(null);
  const navigate = useNavigate();

  const { notifications, allRead, unreadCount, markAsRead } = useNotifications(
    username,
    pharmacyNum,
    userRole
  );
  const [notificationsDismissed, setNotificationsDismissed] = useState(false);

  const fetchPharmacyNum = async (username: string) => {
    try {
      const response = await axios.get('https://tbl.zerdegroup.kz/api/user-info', {
        params: { username }
      });

      if (response.data.success) {
        const newPharmacyNum = response.data.pharmacyNum?.toString();
        setPharmacyNum(newPharmacyNum);
        localStorage.setItem('pharmacyNum', newPharmacyNum);
        setError(null);
      } else {
        setError('Не удалось получить номер аптеки');
        setPharmacyNum(null);
        localStorage.removeItem('pharmacyNum');
      }
    } catch (error) {
      console.error('Ошибка при получении номера аптеки:', error);
      setError('Ошибка при получении данных');
      setPharmacyNum(null);
      localStorage.removeItem('pharmacyNum');
    } finally {
      setLoading(false);
    }
  };

  // Локальная проверка времени без запроса к серверу
  const checkLocalTimeAndLogout = () => {
    const currentTime = new Date();
    const hours = currentTime.getHours();
    const minutes = currentTime.getMinutes();
    const seconds = currentTime.getSeconds();

    if (lastLogoutTime &&
      lastLogoutTime.getHours() === hours &&
      lastLogoutTime.getDate() === currentTime.getDate()) {
      return;
    }

    // Логаут в точное время 10:00 и 22:00
    if ((hours === 10 || hours === 22) && minutes === 0 && seconds === 0) {
      handleLogout();
      setLastLogoutTime(currentTime);
    }
  };

  // Синхронизация с сервером (редко)
  const syncWithServer = async () => {
    try {
      const response = await axios.get('https://tbl.zerdegroup.kz/api/current-time');
      setLockTimer(response.data.lockTimer || '');
    } catch (err) {
      console.error('Ошибка при синхронизации времени:', err);
    }
  };

  useEffect(() => {
    const storedUsername = localStorage.getItem('username');
    const storedPharmacyNum = localStorage.getItem('pharmacyNum');

    if (storedUsername) {
      setUsername(storedUsername);
      if (storedPharmacyNum) {
        setPharmacyNum(storedPharmacyNum);
        setLoading(false);
      } else {
        fetchPharmacyNum(storedUsername);
      }

      // Check if forced profile check is required
      const forceCheck = localStorage.getItem('forceProfileCheck');
      if (forceCheck === 'true') {
        setForceProfileCheck(true);
        setProfileOpen(true);
      }
    } else {
      navigate('/');
    }
  }, [navigate]);

  // Локальная проверка времени каждую секунду (БЕЗ запросов к серверу)
  useEffect(() => {
    const timeCheckInterval = setInterval(checkLocalTimeAndLogout, 1000);
    return () => clearInterval(timeCheckInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Синхронизация с сервером каждые 10 минут
  useEffect(() => {
    syncWithServer(); // Первая синхронизация при загрузке
    const serverSyncInterval = setInterval(syncWithServer, 600000); // 10 минут
    return () => clearInterval(serverSyncInterval);
  }, []);

  const handleLogout = () => {
    clearUserSession();
    navigate('/');
  };

  const isInterfaceBlocked = !username || !pharmacyNum || pharmacyNum === 'NaN' || pharmacyNum === '-';
  const userGuid = localStorage.getItem('guid');

  const getTitle = () => {
    const titles: { [key: number]: string } = {
      0: 'Текущий отчет',
      1: 'Отчет за месяц',
      3: 'Табель РБ',
      4: 'Мониторинг Кассы',
      5: 'Мониторинг КМ',
      6: 'Остатки',
      7: 'Документооборот',
      8: 'ОФД',
      9: 'Номенклатура',
      10: 'Электронный журнал',
      12: 'КМ\'ы'
    };
    return titles[selectedIndex];
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: 'white' }}>
        <CircularProgress sx={{ color: 'black' }} />
      </Box>
    );
  }

  // Панель уведомлений: показываем после гео-проверки, если нет принудительной проверки профиля
  // и есть хоть одно непрочитанное уведомление и пользователь ещё не закрыл панель
  const showNotificationPanel =
    !forceProfileCheck &&
    !notificationsDismissed &&
    !isInterfaceBlocked &&
    !allRead;

  const dashboardContent = (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'white', overflow: 'hidden' }}>

      <DashboardSidebar
        userRole={userRole}
        selectedIndex={selectedIndex}
        onMenuItemClick={setSelectedIndex}
        onLogout={handleLogout}
        isInterfaceBlocked={isInterfaceBlocked}
      />

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>

        <DashboardHeader
          title={getTitle()}
          username={username}
          pharmacyNum={pharmacyNum}
          userRole={userRole}
          unreadNotificationsCount={unreadCount}
          onNotificationsClick={() => setNotificationsDismissed(false)}
          onProfileClick={() => setProfileOpen(true)}
        />

        <Box sx={{
          flexGrow: 1,
          p: 0,
          overflow: 'auto',
          bgcolor: '#ffffff' // Pure white background for content
        }}>
          {isInterfaceBlocked ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
              <Typography variant="h6" color="error">
                Доступ ограничен. Пожалуйста, обратитесь к МАСу или в IT отдел.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ p: 4, height: '100%' }}>
              {selectedIndex === 0 && <CurrentDashboard />}
              {selectedIndex === 1 && <UserStatistic />}
              {selectedIndex === 7 && <Documents />}
              {selectedIndex === 3 && <Timesheet pharmacyNum={pharmacyNum} />}
              {selectedIndex === 4 && <MonitoringCash />}
              {selectedIndex === 5 && <MonitoringKM />}
              {selectedIndex === 6 && <PharmacyMap />}
              {selectedIndex === 9 && <Nomenclature />}
              {selectedIndex === 10 && <OnlineJournal />}
              {selectedIndex === 12 && <UserMarkingCodes />}
            </Box>
          )}
        </Box>
      </Box>

      <UserProfile
        open={profileOpen}
        onClose={() => {
          setProfileOpen(false);
          setForceProfileCheck(false);
        }}
        username={username}
        pharmacyNum={pharmacyNum}
        forceCheck={forceProfileCheck}
        onConfirm={() => {
          localStorage.removeItem('forceProfileCheck');
          setForceProfileCheck(false);
        }}
      />

      {/* Панель уведомлений — рендерится поверх всего через position:fixed внутри компонента */}
      {showNotificationPanel && (
        <NotificationPanel
          notifications={notifications}
          allRead={allRead}
          unreadCount={unreadCount}
          onMarkAsRead={markAsRead}
          onClose={() => setNotificationsDismissed(true)}
        />
      )}

    </Box>
  );

  // Для складов (storage) не требуется подтверждение геолокации
  if (userRole === 'storage') {
    return dashboardContent;
  }

  return (
    <GeolocationManager username={username} guid={userGuid}>
      {dashboardContent}
    </GeolocationManager>
  );
};

export default Dashboard;
